import { useState, useEffect, useContext } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { isLeft } from "fp-ts/lib/Either";
import LocalizationContext from "../../../providers/localization/LocalizationContext";
import ModalsContext from "../../../providers/modal/ModalsContext";
import KeyWordLocalization from "../../../utils/KeyWordLocalization";
import routes from "../../../routes/Routes";
import di from "../../../../DependencyInjection";
import { AutoCompleteItem } from "../../../components/autocompleteWithCheck/AutoCompleteWithCheckProps";
import UserEntity, { UserType } from "../../../../domain/entities/UserEntity";
import SearchUsersUseCase, { SearchUsersUseCaseName } from "../../../../domain/use_cases/user/SearchUsersUseCase";
import GetUserByIdUseCase, { GetUserByIdUseCaseName } from "../../../../domain/use_cases/user/GetUserByIdUseCase";
import GetUserReportUseCase, { GetUserReportUseCaseName } from "../../../../domain/use_cases/report/GetUserReportUseCase";
import { GetUserReportResponse } from "../../../../domain/repositories/ReportRepository";
import { ShiftStatus } from "../../../../domain/entities/ShiftEntity";
import * as XLSX from 'xlsx';

const useValetReportViewModel = () => {
    const [searchParams] = useSearchParams();
    const valetQuery = searchParams.get("valet");
    const startDateQuery = searchParams.get("startDate");
    const endDateQuery = searchParams.get("endDate");

    const { i18n } = useContext(LocalizationContext);
    const { addToast } = useContext(ModalsContext);
    const formInfo = useForm();
    const { register, handleSubmit, getValues, setValue, watch, formState: { errors } } = formInfo;
    const [reportData, setReportData] = useState<GetUserReportResponse | undefined>(undefined);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    // Función para buscar valets
    const _onSearchValets = async (query: string): Promise<AutoCompleteItem<UserEntity>[]> => {
        const response = await di.get<SearchUsersUseCase>(SearchUsersUseCaseName).call({
            query, 
            page: 1,
            itemsPerPage: 20,
            role: UserType.VALET
        });
        if (isLeft(response)) return [];
        const parsed = response.right.data.map((item) => {
            return {
                label: `${item.name} - ${item.email || 'Sin email'}`,
                id: item.id,
                aditionalValue: item,
            };
        });
        return parsed;
    };

    // Función para cargar el valet por ID cuando viene de la URL
    const _loadValet = async () => {
        if (valetQuery && (!getValues()[KeyWordLocalization.ValetReportPageValet] || getValues()[KeyWordLocalization.ValetReportPageValet].length === 0)) {
            const response = await di.get<GetUserByIdUseCase>(GetUserByIdUseCaseName).call(valetQuery);
            if (isLeft(response)) return;
            const parsed: AutoCompleteItem<UserEntity> = {
                label: `${response.right.name} - ${response.right.email || 'Sin email'}`,
                id: response.right.id,
                aditionalValue: response.right,
            };
            setValue(KeyWordLocalization.ValetReportPageValet, [parsed]);
        }
    };

    // Función para validar fechas
    const validateDates = (startDate: string, endDate: string): boolean => {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const now = new Date();
        const twoMonthsAgo = new Date();
        twoMonthsAgo.setMonth(now.getMonth() - 2);

        // Validar que fecha inicial no sea mayor a 2 meses
        if (start < twoMonthsAgo) {
            addToast(i18n("La fecha inicial no puede ser mayor a 2 meses atrás"), "error", undefined);
            return false;
        }

        // Validar que fecha inicial no sea mayor a fecha final
        if (start > end) {
            addToast(i18n("La fecha inicial no puede ser mayor a la fecha final"), "error", undefined);
            return false;
        }

        // Validar que las fechas no sean futuras
        if (start > now || end > now) {
            addToast(i18n("Las fechas no pueden ser futuras"), "error", undefined);
            return false;
        }

        return true;
    };

    // Función para cargar el reporte
    const _loadReport = async () => {
        const data = getValues();
        const valet = data[KeyWordLocalization.ValetReportPageValet]?.[0]?.aditionalValue;
        const startDate = data[KeyWordLocalization.ValetReportPageStartDate];
        const endDate = data[KeyWordLocalization.ValetReportPageEndDate];

        if (!valet) {
            addToast(i18n("Debe seleccionar un valet"), "error", undefined);
            return;
        }

        if (!startDate || !endDate) {
            addToast(i18n("Debe seleccionar fechas de inicio y fin"), "error", undefined);
            return;
        }

        if (!validateDates(startDate, endDate)) {
            return;
        }

        setLoading(true);
        setReportData(undefined);

        try {
            const response = await di.get<GetUserReportUseCase>(GetUserReportUseCaseName).call({
                userId: valet.id,
                role: UserType.VALET,
                startDate: new Date(startDate),
                endDate: new Date(endDate),
            });

            console.log("Report response:", response);

            if (isLeft(response)) {
                addToast(i18n("Error al cargar el reporte"), "error", undefined);
                setReportData(undefined);
            } else {
                setReportData(response.right);
            }
        } catch (error) {
            console.error("Error loading report:", error);
            addToast(i18n("Error al cargar el reporte"), "error", undefined);
            setReportData(undefined);
        }

        setLoading(false);
    };

    // Función para descargar el reporte en Excel
    const _downloadReport = async () => {
        const data = getValues();
        const valet = data[KeyWordLocalization.ValetReportPageValet]?.[0]?.aditionalValue;
        const startDate = data[KeyWordLocalization.ValetReportPageStartDate];
        const endDate = data[KeyWordLocalization.ValetReportPageEndDate];

        if (!reportData) {
            addToast(i18n("Debe generar el reporte primero"), "error", undefined);
            return;
        }

        if (!valet) {
            addToast(i18n("Debe seleccionar un valet"), "error", undefined);
            return;
        }

        if (!startDate || !endDate) {
            addToast(i18n("Debe seleccionar fechas de inicio y fin"), "error", undefined);
            return;
        }

        setLoading(true);

        try {

            // Crear el workbook
            const workbook = XLSX.utils.book_new();

            // Hoja 1: Resumen
            const summaryData = [
                ['Reporte de Valet'],
                [''],
                ['Valet:', valet.name],
                ['Email:', valet.email || 'Sin email'],
                ['Período:', `${startDate} - ${endDate}`],
                ['Fecha de generación:', new Date().toLocaleDateString('es-ES')],
                [''],
                ['RESUMEN GENERAL'],
                ['Total de Turnos:', (reportData.assistedShifts + reportData.missedShifts) || 0],
                ['Turnos Asistidos:', reportData.assistedShifts || 0],
                ['Turnos Rechazados:', reportData.missedShifts || 0],
                ['Horas Trabajadas:', `${reportData.workedHours || 0} horas`],
                [''],
            ];

            const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
            XLSX.utils.book_append_sheet(workbook, summarySheet, 'Resumen');

            // Hoja 2: Detalle de Turnos
            if (reportData.shifts && reportData.shifts.length > 0) {
                const shiftsData = [
                    ['Detalle de Turnos'],
                    [''],
                    ['Fecha', 'Establecimiento', 'Dirección', 'Horario Inicio', 'Horario Fin', 'Estado', 'Check In', 'Check Out']
                ];

                reportData.shifts.forEach(shift => {
                    // Mapear estados a las nuevas etiquetas
                    let statusLabel = 'No Confirmado';
                    if (shift.status === ShiftStatus.FINISHED) {
                        statusLabel = 'Completado';
                    } else if (shift.status === ShiftStatus.REJECTED) {
                        statusLabel = 'Rechazado';
                    } else {
                        statusLabel = 'No Confirmado';
                    }

                    shiftsData.push([
                        shift.startDate.toLocaleDateString('es-ES'),
                        shift.stablishment.name,
                        shift.stablishment.address,
                        shift.startDate.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
                        shift.endDate?.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) || 'No definido',
                        statusLabel,
                        shift.checkedInAt ? shift.checkedInAt.toLocaleString('es-ES') : 'No registrado',
                        shift.checkedOutAt ? shift.checkedOutAt.toLocaleString('es-ES') : 'No registrado'
                    ]);
                });

                const shiftsSheet = XLSX.utils.aoa_to_sheet(shiftsData);
                XLSX.utils.book_append_sheet(workbook, shiftsSheet, 'Detalle de Turnos');
            }

            // Descargar el archivo
            const fileName = `reporte_valet_${valet.name.replace(/\s+/g, '_')}_${startDate}_${endDate}.xlsx`;
            XLSX.writeFile(workbook, fileName);

            addToast(i18n("Reporte descargado exitosamente"), "success", undefined);

        } catch (error) {
            console.error("Error downloading report:", error);
            addToast(i18n("Error al descargar el reporte"), "error", undefined);
        }

        setLoading(false);
    };

    const _handleSearch = (data: any) => {
        const valet = data[KeyWordLocalization.ValetReportPageValet]?.[0]?.id;
        const startDate = data[KeyWordLocalization.ValetReportPageStartDate];
        const endDate = data[KeyWordLocalization.ValetReportPageEndDate];
        
        let query = "?";
        if (valet) query += `valet=${valet}&`;
        if (startDate) query += `startDate=${startDate}&`;
        if (endDate) query += `endDate=${endDate}&`;
        if (query.endsWith("&")) query = query.slice(0, -1);
        
        navigate(routes.valet_report.relativePath + query);
    };

    const _handleChange = () => {
        const data = getValues();
        _handleSearch(data);
    };

    const _handleLoad = async () => {
        // Cargar fechas por defecto si no vienen de la URL
        if (!startDateQuery) {
            const defaultStart = new Date();
            defaultStart.setDate(defaultStart.getDate() - 30);
            setValue(KeyWordLocalization.ValetReportPageStartDate, defaultStart.toISOString().split('T')[0]);
        } else {
            setValue(KeyWordLocalization.ValetReportPageStartDate, startDateQuery);
        }

        if (!endDateQuery) {
            const defaultEnd = new Date();
            setValue(KeyWordLocalization.ValetReportPageEndDate, defaultEnd.toISOString().split('T')[0]);
        } else {
            setValue(KeyWordLocalization.ValetReportPageEndDate, endDateQuery);
        }

        // Cargar el valet desde la URL
        await _loadValet();
        
        // Cargar reporte después de que todo esté configurado
        setTimeout(() => {
            const valet = getValues()[KeyWordLocalization.ValetReportPageValet];
            const startDate = getValues()[KeyWordLocalization.ValetReportPageStartDate];
            const endDate = getValues()[KeyWordLocalization.ValetReportPageEndDate];
            
            if (valet && valet.length > 0 && startDate && endDate) {
                _loadReport();
            }
        }, 100);
    };

    // Función para manejar cambio de valet del AutoComplete
    const handleChangeValet = (item: AutoCompleteItem<UserEntity>) => {
        setValue(KeyWordLocalization.ValetReportPageValet, item ? [item] : []);
        _handleChange();
    };

    // Función para manejar cambio de fechas
    // Calcular fechas límite
    const getMinDate = (): string => {
        const twoMonthsAgo = new Date();
        twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);
        return twoMonthsAgo.toISOString().split('T')[0];
    };

    const getMaxDate = (): string => {
        return new Date().toISOString().split('T')[0];
    };

    useEffect(() => {
        _handleLoad();
    }, [valetQuery, startDateQuery, endDateQuery]);

    return {
        formInfo,
        reportData,
        loading,
        i18n,
        _handleSearch,
        _handleChange,
        _loadReport,
        _downloadReport,
        _onSearchValets,
        handleChangeValet,
        getMinDate,
        getMaxDate,
        errors,
        register,
        handleSubmit,
    };
};

export default useValetReportViewModel;